import * as _ from 'lodash'
import util from './util';
import serviceGateway from './serviceGateway';
import store from './store';
import * as globalActions from './actions';
import contractorModel from './models/contractorModel';
import jobCostModel from './models/jobCostModel';
import socketClient from './socketClient';

function processContractorResult (contractors) {
	const model = store().get('model');

	model.withMutations(map => {
		const jobCosts:any[] = [];

		_.forEach(contractors, c => {
			// map the jobCosts to an array of keys and store them until. . .
			if (c.templates) {
				c.jobCostKeys = _.map(c.templates, jc => {
					jobCosts.push(jc);
					return jc.key;
				});
			}
			const cModel = contractorModel(c);
			map.setIn(['contractors', c.key], cModel);
		});

		// . . . Now update or insert the jobCosts in the store
		_.forEach(jobCosts, jc => {
			const existing = map.getIn(['jobCosts', jc.key]);

			// only set partial jobCost if it doesn't exist or the itemVersion has changed
			if (!existing || existing.itemVersion <= jc.itemVersion) {
				map.setIn(['jobCosts', jc.key], jobCostModel(jc));
			}
		});
	});
}

export const socketClientHandlers = {
	['model.contractor.added']: message => {
		fetchContractor({ key: message.payload.key });
	},
	['model.contractor.updated']: message => {
		const keyPath = ['model', 'contractors', message.payload.key];
		const existing = store().getIn(keyPath);

		if (existing) {
			if (existing.itemVersion === message.payload.patchTargetVersion) {
				existing.applyPatch(message.payload.patch);
				store().setIn(keyPath, existing);
			}
			else if (existing.itemVersion < message.payload.patchTargetVersion) {
				fetchContractor({ key: message.payload.key });
			}
		}
		else {
			fetchContractor({ key: message.payload.key });
		}
	}
};

// wrap handlers to allow extensions to override
for (const key of _.keys(socketClientHandlers)) {
	socketClient.on(key, message => {
		socketClientHandlers[key](message);
	});
}

// TODO: handle model.contractor.removed message

// NOTE: Action returns are only to allow tests to access the promise. DO NOT use return values in runtime code.
export const fetchContractor = params => {
	return serviceGateway.fetchContractor(params)
		.then(result => {
			if (result.contractor) {
				processContractorResult([result.contractor]);
			}
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const fetchContractors = params => {
	return serviceGateway.searchContractors(params)
		.then(result => {
			processContractorResult(result.contractors);
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const searchContractors = params => {
	return serviceGateway.searchContractors(params)
		.then(result => {
			return _.map(result.contractors, c => {
				return contractorModel(c);
			});
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const saveContractor = params => {
	const keyPath = ['model', 'contractors', params.contractor.key];

	store().setIn(keyPath, contractorModel(params.contractor));
	return serviceGateway.saveContractor({ contractor: params.contractor, patch: params.contractor.dateCreated ? params.patch : undefined })
		.then(result => {
			processContractorResult([result.contractor]);
		})
		.catch(err => {
			const key = util.generateGuid();
			const retry = p => {
				globalActions.clearAlert({ key: key });
				return saveContractor(p || params);
			};

			globalActions.pushAlert({
				key: key,
				type: 'error',
				message: err.message || 'Server Error',
				source: 'server',
				action: 'saveContractor',
				retry: retry,
				params: params,
				orgininalError: err,
				isCloseable: true
			});
		});
};

export const deleteContractor = params => {
	const removedJobCosts:any[] = [];

	return serviceGateway.deleteContractor(params)
		.then(() => {
			const model = store().get('model');

			model.withMutations(map => {
				const jobCosts = map.get('jobCosts').filter(jc => jc.contractorKey === params.contractor.key);

				jobCosts.forEach(jc => {
					map = map.deleteIn(['jobCosts', jc.key]);
					removedJobCosts.push(jc);
				});

				map.deleteIn(['contractors', params.contractor.key]);
			});
		})
		.catch(err => {
			const model = store().get('model');

			model.withMutations(map => {
				_.forEach(removedJobCosts, jc => {
					map = map.setIn(['jobCosts', jc.key], jc);
				});

				map.setIn(['contractors', params.contractor.key], params.contractor);
			});

			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};